/**
 * Dropdown selector for choosing action types when creating or editing actions.
 * Dynamically loads icons from registered action plugins.
 */
import {
	getActionIcon,
	registerFallbackIcon,
} from "@nucleus/action-plugins/frontend";
import { ExternalLink, FormInput, MessageSquare, Zap } from "lucide-react";
import type { ComponentType } from "react";



import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";

// Register fallback icons for base action types
// These are used when a plugin doesn't provide a custom icon
registerFallbackIcon("comment", MessageSquare);
registerFallbackIcon("input", FormInput);
registerFallbackIcon("automation", Zap);
registerFallbackIcon("link", ExternalLink);

interface ActionTypeSelectorProps {
	value: string | null;
	onValueChange: (value: string) => void;
	placeholder?: string;
}

/**
 * Get the icon for an action type, using plugin registry with fallbacks.
 * @param slug - The action type slug
 * @param type - The base action type for fallback
 * @returns The icon component
 */
function getIconForActionType(slug: string, type: string): ComponentType<{ className?: string }> {
	// Try plugin registry first, then fallback
	const icon = getActionIcon(slug, type);
	return icon ?? MessageSquare;
}

/**
 * Dropdown selector for choosing an action type.
 * @param props - component props
 * @param props.value - current selected value
 * @param props.onValueChange - callback when value changes
 * @param props.placeholder - placeholder text when no value selected
 */
export function ActionTypeSelector({ value, onValueChange, placeholder = "Select action type..." }: ActionTypeSelectorProps) {
	const actionTypesQuery = trpc.actions.listActionTypes.useQuery();

	const actionTypes = actionTypesQuery.data ?? [];

	return (
		<Select value={value ?? ""} onValueChange={onValueChange}>
			<SelectTrigger>
				<SelectValue placeholder={placeholder} />
			</SelectTrigger>
			<SelectContent>
				{actionTypes.map((actionType) => {
					const Icon = getIconForActionType(actionType.slug, actionType.type);
					return (
						<SelectItem key={actionType.id} value={actionType.id}>
							<div className="flex items-center gap-2">
								<Icon className="h-4 w-4" />
								<span>{actionType.name}</span>
							</div>
						</SelectItem>
					);
				})}
			</SelectContent>
		</Select>
	);
}

/**
 * Returns the appropriate icon component for an action type.
 * Uses the plugin registry with fallbacks.
 * @param type - the action type category
 * @param slug - optional specific action slug for custom icons
 * @returns The icon component
 */
export function getActionTypeIcon(type: string, slug?: string): ComponentType<{ className?: string }> {
	if (slug) {
		return getIconForActionType(slug, type);
	}
	const icon = getActionIcon("", type);
	return icon ?? MessageSquare;
}
